import type { Bundle } from "../schema/store.js";
import type { ScoreSource } from "../schema/events.js";

const sp = (type: string, minutes: number, qty: number) => ({ itemId: `itm.speedup_${type}_${minutes}`, qty });
const crate = (res: string, size: string, qty: number) => ({ itemId: `itm.crate_${res}_${size}`, qty });
const N = (de: string, en: string) => ({ de, en });

export interface LuckyMissionDef {
  id: string;
  name: { de: string; en: string };
  source: ScoreSource;
  target: number;
  /** Tickets (Wild Memory) or keys (Key Gacha) the mission pays once a day. */
  reward: number;
}

export interface LuckyPrizeDef {
  id: string;
  weight: number;
  reward: Bundle;
}

/** Wild Memory (60-store.md R99): ten tiles shown for three seconds, covered and shuffled, five draws a day. */
export const WILD_MEMORY = {
  tiles: 10,
  revealMs: 3_000,
  /** Ticket price of draw 1..5 of the day: the first is free. */
  ticketCosts: [0, 1, 2, 3, 4] as readonly number[],
  grandEvery: 20,
  ticketItem: "itm.wild_ticket",
  /** Tickets for Credits in the hub (the "pack"), once a day. */
  pack: { tickets: 5, price: 120, perDay: 1 },
} as const;

export const WILD_MEMORY_PRIZES: readonly LuckyPrizeDef[] = [
  { id: "wm.ufr_s", weight: 6, reward: { items: [{ itemId: "itm.ufr_s", qty: 5 }] } },
  { id: "wm.ufr_a", weight: 12, reward: { items: [{ itemId: "itm.ufr_a", qty: 10 }] } },
  { id: "wm.speed_60", weight: 16, reward: { items: [sp("general", 60, 2)] } },
  { id: "wm.speed_build", weight: 14, reward: { items: [sp("build", 180, 1)] } },
  { id: "wm.rat", weight: 16, reward: { items: [crate("rat", "100k", 1)] } },
  { id: "wm.pol", weight: 16, reward: { items: [crate("pol", "100k", 1)] } },
  { id: "wm.aly", weight: 10, reward: { items: [crate("aly", "10k", 2)] } },
  { id: "wm.fab", weight: 8, reward: { items: [{ itemId: "itm.fab", qty: 1 }] } },
  { id: "wm.ffab", weight: 2, reward: { items: [{ itemId: "itm.ffab", qty: 1 }] } },
];

/** Every twentieth draw (lifetime) pays this on top of the tile. */
export const WILD_MEMORY_GRAND: Bundle = { items: [{ itemId: "itm.ufr_s", qty: 30 }, { itemId: "itm.ffab", qty: 2 }], credits: 100 };

export const WILD_MEMORY_MISSIONS: readonly LuckyMissionDef[] = [
  { id: "wm.m.packs", name: N("Zwei Rudel besiegen", "Defeat two packs"), source: "pack.kill", target: 2, reward: 1 },
  { id: "wm.m.gather", name: N("20.000 Ressourcen sammeln", "Gather 20,000 resources"), source: "gather", target: 20_000, reward: 1 },
  { id: "wm.m.build", name: N("Ein Gebäude ausbauen", "Upgrade a building"), source: "build.level", target: 1, reward: 1 },
];

export type GachaChest = "bronze" | "silver" | "gold";

export interface GachaWeekDef {
  id: string;
  name: { de: string; en: string };
  /** One key: a prize from this pool. */
  bronze: readonly LuckyPrizeDef[];
  /** From the tenth single opening of the week on, one key rolls this pool. */
  silver: readonly LuckyPrizeDef[];
  /** Ten keys at once. */
  gold: Bundle;
}

/** Key Gacha (60-store.md R99, event `ev.key_gacha`, Friday to Sunday): the week picks the ladder. */
export const KEY_GACHA = {
  eventId: "ev.key_gacha",
  keyItem: "itm.gacha_key",
  goldKeys: 10,
  /** Single openings of the week from which a key rolls the silver pool. */
  silverFrom: 10,
  /** Keys for Credits in the hub, once a day while the event runs. */
  pack: { keys: 5, price: 150, perDay: 1 },
} as const;

export const KEY_GACHA_WEEKS: readonly GachaWeekDef[] = [
  {
    id: "kg.fragments",
    name: N("Fragmentwoche", "Fragment week"),
    bronze: [
      { id: "kg.f.ufr_b", weight: 40, reward: { items: [{ itemId: "itm.ufr_b", qty: 10 }] } },
      { id: "kg.f.ufr_a", weight: 30, reward: { items: [{ itemId: "itm.ufr_a", qty: 5 }] } },
      { id: "kg.f.cdc", weight: 25, reward: { items: [{ itemId: "itm.cdc", qty: 20 }] } },
      { id: "kg.f.ufr_s", weight: 5, reward: { items: [{ itemId: "itm.ufr_s", qty: 3 }] } },
    ],
    silver: [
      { id: "kg.f.s.ufr_a", weight: 50, reward: { items: [{ itemId: "itm.ufr_a", qty: 10 }] } },
      { id: "kg.f.s.ufr_s", weight: 35, reward: { items: [{ itemId: "itm.ufr_s", qty: 5 }] } },
      { id: "kg.f.s.fab", weight: 15, reward: { items: [{ itemId: "itm.fab", qty: 2 }] } },
    ],
    gold: { items: [{ itemId: "itm.ufr_s", qty: 25 }, { itemId: "itm.ufr_a", qty: 30 }, { itemId: "itm.chest_gold", qty: 1 }] },
  },
  {
    id: "kg.speed",
    name: N("Tempowoche", "Speed week"),
    bronze: [
      { id: "kg.s.sp15", weight: 40, reward: { items: [sp("general", 15, 3)] } },
      { id: "kg.s.sp60", weight: 30, reward: { items: [sp("general", 60, 1)] } },
      { id: "kg.s.rat", weight: 25, reward: { items: [crate("rat", "10k", 3)] } },
      { id: "kg.s.sp480", weight: 5, reward: { items: [sp("general", 480, 1)] } },
    ],
    silver: [
      { id: "kg.s.s.sp180", weight: 50, reward: { items: [sp("general", 180, 1)] } },
      { id: "kg.s.s.sp480", weight: 35, reward: { items: [sp("general", 480, 1)] } },
      { id: "kg.s.s.fab", weight: 15, reward: { items: [{ itemId: "itm.fab", qty: 2 }] } },
    ],
    gold: { items: [sp("general", 1440, 2), sp("build", 480, 2), { itemId: "itm.chest_gold", qty: 1 }] },
  },
  {
    id: "kg.fabrication",
    name: N("Fabrikationswoche", "Fabrication week"),
    bronze: [
      { id: "kg.b.cdc", weight: 40, reward: { items: [{ itemId: "itm.cdc", qty: 20 }] } },
      { id: "kg.b.pol", weight: 30, reward: { items: [crate("pol", "10k", 3)] } },
      { id: "kg.b.ufr_a", weight: 25, reward: { items: [{ itemId: "itm.ufr_a", qty: 5 }] } },
      { id: "kg.b.fab", weight: 5, reward: { items: [{ itemId: "itm.fab", qty: 1 }] } },
    ],
    silver: [
      { id: "kg.b.s.fab", weight: 55, reward: { items: [{ itemId: "itm.fab", qty: 1 }] } },
      { id: "kg.b.s.ufr_s", weight: 35, reward: { items: [{ itemId: "itm.ufr_s", qty: 5 }] } },
      { id: "kg.b.s.ffab", weight: 10, reward: { items: [{ itemId: "itm.ffab", qty: 1 }] } },
    ],
    gold: { items: [{ itemId: "itm.ffab", qty: 3 }, { itemId: "itm.fab", qty: 5 }, { itemId: "itm.chest_gold", qty: 1 }, { itemId: "itm.proto_blueprint", qty: 1 }] },
  },
];

/** D140: the Fortune Vault's daily missions pay Vault Keys — the main way to shoot, five a day. */
export const FORTUNE_VAULT_MISSIONS: readonly LuckyMissionDef[] = [
  { id: "fv.m.login", name: N("Einloggen", "Log in"), source: "login", target: 1, reward: 1 },
  { id: "fv.m.arena", name: N("Zwei Arena-Kämpfe", "Two arena fights"), source: "arena.fight", target: 2, reward: 2 },
  { id: "fv.m.packs", name: N("Drei Rudel besiegen", "Defeat three packs"), source: "pack.kill", target: 3, reward: 2 },
];

/** The week's missions of the Key Gacha: they count only while the event runs and pay keys once a day. */
export const KEY_GACHA_MISSIONS: readonly LuckyMissionDef[] = [
  { id: "kg.m.login", name: N("Einloggen", "Log in"), source: "login", target: 1, reward: 2 },
  { id: "kg.m.packs", name: N("Drei Rudel besiegen", "Defeat three packs"), source: "pack.kill", target: 3, reward: 2 },
  { id: "kg.m.stamina", name: N("50 Ausdauer einsetzen", "Spend 50 stamina"), source: "stamina.spent", target: 50, reward: 3 },
];

/** Cumulative openings of the week that pay a milestone (the event's milestones, paid once each). */
export const KEY_GACHA_MILESTONES: readonly { points: number; reward: Bundle }[] = [
  { points: 10, reward: { items: [{ itemId: "itm.fab", qty: 2 }, sp("general", 60, 3)] } },
  { points: 30, reward: { items: [{ itemId: "itm.ffab", qty: 1 }, { itemId: "itm.ufr_a", qty: 20 }] } },
  { points: 60, reward: { items: [{ itemId: "itm.ffab", qty: 3 }, { itemId: "itm.ufr_s", qty: 20 }], credits: 200 } },
];
